import { Fragment, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, ArrowRight, BookOpen, Bone, Brain, Check, Droplet, Eye, FileText, FlaskConical, Footprints, Heart, Layers, ListChecks,
  Lock, MessageSquareText, MoveHorizontal, Pill, Ruler, Search, ShieldCheck, Wind, Zap,
} from "lucide-react";
import { VIDEOS } from "./sai/DemoVideo";
import SaiProductFrame, { nextProductAfter } from "./sai/SaiProductFrame";
import Showcase from "./sai/Showcase";
import StepTabs from "./sai/StepTabs";
import Flipbook from "./Flipbook";
import { IDS } from "./smartReportIds";
import "./SmartReports.css";

// Smart Reports, ported from sai's SmartReportScreen (brochure-demo @ 2a7c405)
// into our shell: our <Screen> via SaiProductFrame, data-trapezium-* IDs via
// toTrapezium, "Next product" and the full-report link to our routes.
//
// His notes: the founder's page, rebuilt: five steps over real report
// pages. Why a PDF (a book to turn), three lengths (a book per template, with a
// switch), the whole pack (seven specialised
// layouts; Microbiology turns two pages), prints in mono (drag to compare), and
// the premium pages as a fan of cards (founder's call, 2026-10).
// Content: the founder's smart-reports section. The report pages (cover, body,
// quick summary and the three templates) come from the full sample reports of
// one demo patient (Rohan Mehta). Each template shows its cover, four pages,
// then a locked page: the full report is shared by the team, not downloaded.

const SCREEN = "smart-report"; // his screen key
const SCREEN_ID = "product.smart-reports";
const IMG = (k) => `/demo/smart-report/${k}.jpg`;
// What each sample page shows, in words (for the assistant and screen readers;
// the pages are pictures). All from demo patients.
const ABOUT = {
  cover: "Report cover: Smart Health Report for the demo patient.",
  han3: "Quick health summary: 20 abnormal, 0 borderline and 42 normal tests, then each body system with its out-of-range results.",
  per1: "Blood counts: what the profile measures, each low result on a range bar, and what an abnormal result may indicate.",
  per2: "Blood counts, continued: a high result on its range bar, what it may indicate, and diet and lifestyle suggestions.",
  per3: "Lipid profile: a heart-risk gauge, a QR code to the app, and trend charts for total cholesterol and triglycerides.",
  per4: "Personalised diet recommendations: foods to avoid and foods to have, based on the results.",
  cmp1: "Blood counts, compact: one line per test with its status, result and range bar.",
  cmp2: "Liver profile, compact: one line per test with its status, result and range bar.",
  cmp3: "Lipid profile, compact: a heart-risk gauge, a QR code and cholesterol trend charts.",
  cmp4: "Thyroid profile, compact: a thyroid-risk gauge, a QR code and one line per thyroid test.",
  tot1: "Blood counts, total: every marker explained, the normal ones too, each on a range bar.",
  tot2: "Liver markers, total: each marker explained, with its range bar.",
  tot3: "Cholesterol, total: an LDL trend chart, then non-HDL and HDL cholesterol explained on range bars.",
  tot4: "Thyroid profile, total: a thyroid-risk gauge, a QR code, and T3 and T4 explained on range bars.",
  micro1: "Microbiology report: stool microscopy findings shown as dot scales.",
  micro2: "Microbiology report: a clinical signal summary and what the report means.",
  alg: "Allergy panel: inhaled allergens, pollens and plants, each with a High or Normal tag, names in two languages.",
  thy: "Thyroid profile: T4 on a range bar, with the symptoms of hyperthyroidism shown as icons.",
  fert: "Fertility profile: hormones such as estradiol and androstenedione, with ranges by cycle phase.",
  canc: "Cancer profile: screening markers such as PSA, CA 125 and CA 15-3 on range bars.",
  cardiac: "Cardiac profile: troponin on a range bar, homocysteine, and diet and lifestyle tips.",
  rad: "Radiology screening analysis: X-ray and 2D echo impressions in words, and the doctor's recommendation.",
};
const Page = ({ k }) => <img src={IMG(k)} alt={ABOUT[k] || ""} width="760" height="1076" decoding="async" />;

const FIGURES = [["1,500+", "biomarkers"], ["10M+", "reports delivered"], ["4.9/5", "rated by patients"]];

const TEMPLATES = [
  { key: "personal", name: "Personal", of: 28, n: "01", foot: "Abnormal in depth",
    meta: ["Abnormal-first", "Most chosen"], pages: ["per1", "per2", "per3", "per4"], lock: "per-lock" },
  { key: "compact", name: "Compact", of: 28, n: "02", foot: "One line each",
    meta: ["Every marker", "Range bar inline"], pages: ["cmp1", "cmp2", "cmp3", "cmp4"], lock: "cmp-lock" },
  { key: "total", name: "Total", of: 38, n: "03", foot: "Every marker explained",
    meta: ["Normals explained too", "Premium packages"], pages: ["tot1", "tot2", "tot3", "tot4"], lock: "tot-lock" },
];

const DECK = [
  { key: "allergy", img: "alg", name: "Allergy", sub: "Bilingual allergen panel" },
  { key: "hormones", img: "thy", name: "Hormones", sub: "Thyroid, with symptoms" },
  { key: "female-health", img: "fert", name: "Female health", sub: "Fertility profile" },
  { key: "cancer-markers", img: "canc", name: "Cancer markers", sub: "Screening panel" },
  { key: "cardiac", img: "cardiac", name: "Cardiac", sub: "Risk profile" },
  { key: "radiology", img: "rad", name: "Radiology", sub: "Impressions, not values" },
  { key: "microbiology", pages: ["micro1", "micro2"], name: "Microbiology", sub: "Culture analysis" },
];

const STEPS = [
  { key: "pdf", nav: "Why a PDF", head: "Healthcare still runs on the PDF.",
    lede: "Every hospital and insurer runs on PDF. We made it worth reading.",
    points: [["Opens on any phone", "Prints at any desk"], ["Every hospital accepts it", "And every insurer"], ["White-labelled", "Down to the barcode"]] },
  { key: "lengths", nav: "Lengths", head: "Same result. Three depths.",
    lede: "Identical values, three templates. Pick one." },
  { key: "pack", nav: "Specialty", head: "And far beyond routine pathology.",
    lede: "Each panel gets its own layout. Pick any one." },
  { key: "mono", nav: "Mono print", head: "Every report also prints in black and white.",
    lede: "A second design, not a downgrade. Drag to compare.",
    points: [["A second design", "Not a greyscale copy"], ["Every template", "And every profile"], ["Readable on any printer", "Status still clear without colour"]] },
  { key: "premium", nav: "Personalised Insights", head: "Personalised insights, for the packages you sell most.",
    lede: "Four pages that turn a report into a health plan. Pick one." },
];

// The premium pages (from the demo patient's Personal report), shown as a fan.
const PREMIUM = [
  { key: "consolidated", img: "consolidated", name: "Wellness score", sub: "One number out of 100" },
  { key: "quick-health", img: "han3", name: "Quick health summary", sub: "Every profile at a glance" },
  { key: "body-summary", img: "body", name: "Body summary", sub: "The whole body, on one page" },
  { key: "diet", img: "per4", name: "Diet plan", sub: "From their own results" },
];

// The picked page sits in front; the others fan out behind it, left and right.
function Fan({ picked }) {
  const at = PREMIUM.findIndex((p) => p.key === picked);
  return (
    <div className="sr-fan" aria-hidden="true">
      {PREMIUM.map((p, i) => {
        const d = i - at;
        return (
          <span
            key={p.key}
            className="sr-fan-card"
            data-on={d === 0 ? "true" : undefined}
            style={{ "--r": `${d * 7}deg`, "--x": `${d * 22}%`, "--y": `${Math.abs(d) * 3}%`, zIndex: 10 - Math.abs(d) }}
          >
            <Page k={p.img} />
          </span>
        );
      })}
    </div>
  );
}

// The template's cover, in the style of the real report covers (Report Engine
// covers and the configuration portal's cover designs): white, the brand's
// blues, "Smart Health Report", an illustration and three short promises. No
// patient details and no result colours on a cover. Each template has its own
// illustration: Personal a person under the magnifier, Compact a tidy page,
// Total the whole body with every system around it.
const COVER_PROMISES = {
  personal: [[Search, "What needs attention, first"], [MessageSquareText, "Explained in plain words"], [Footprints, "Clear next steps"]],
  compact: [[ListChecks, "Every marker, one line"], [Ruler, "The range at a glance"], [Zap, "Quick to read"]],
  total: [[BookOpen, "Every marker explained"], [ShieldCheck, "Normal results too"], [Layers, "The most detailed"]],
};
const ORBIT = [Heart, Brain, Droplet, Bone, Eye, Wind, FlaskConical, Pill];

function CoverArt({ k }) {
  if (k === "personal") {
    return (
      <svg className="sr-art" viewBox="0 0 200 150" aria-hidden="true">
        <circle cx="100" cy="78" r="66" fill="#EEF4FF" />
        <circle cx="100" cy="78" r="48" fill="#E0EBFF" />
        <circle cx="92" cy="62" r="18" fill="#3567D6" />
        <path d="M58 128c2-24 16-38 34-38s32 14 34 38z" fill="#3567D6" />
        <circle cx="130" cy="92" r="20" fill="#fff" stroke="#3CD0FF" strokeWidth="6" />
        <path d="M144 106l18 18" stroke="#3CD0FF" strokeWidth="8" strokeLinecap="round" />
        <path d="M122 92h16M130 84v16" stroke="#3567D6" strokeWidth="4" strokeLinecap="round" />
        <circle cx="40" cy="40" r="4" fill="#3CD0FF" /><circle cx="168" cy="44" r="3" fill="#3567D6" /><circle cx="160" cy="24" r="5" fill="#D6E4FF" />
      </svg>
    );
  }
  if (k === "compact") {
    return (
      <svg className="sr-art" viewBox="0 0 200 150" aria-hidden="true">
        <rect x="52" y="14" width="96" height="128" rx="10" fill="#E0EBFF" transform="rotate(-6 100 78)" />
        <rect x="56" y="10" width="96" height="128" rx="10" fill="#fff" stroke="#D6E2F7" strokeWidth="2" />
        <rect x="68" y="24" width="44" height="7" rx="3.5" fill="#3567D6" />
        {[44, 62, 80, 98, 116].map((y, i) => (
          <g key={y}>
            <rect x="68" y={y} width="30" height="5" rx="2.5" fill="#B9C9E8" />
            <rect x="104" y={y} width="36" height="5" rx="2.5" fill="#EEF4FF" />
            <circle cx={112 + ((i * 7) % 20)} cy={y + 2.5} r="4" fill={i % 2 ? "#3CD0FF" : "#3567D6"} />
          </g>
        ))}
        <circle cx="160" cy="30" r="14" fill="#3CD0FF" />
        <path d="M153 30l5 5 9-10" stroke="#fff" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="36" cy="110" r="5" fill="#D6E4FF" /><circle cx="42" cy="36" r="3" fill="#3CD0FF" />
      </svg>
    );
  }
  return (
    <div className="sr-art sr-orbit" aria-hidden="true">
      <span className="sr-orbit-ring" />
      <span className="sr-orbit-core"><Activity size={26} strokeWidth={2.2} /></span>
      {ORBIT.map((Icon, i) => (
        <span key={i} className="sr-orbit-node" style={{ "--a": `${(i * 360) / ORBIT.length - 90}deg` }}>
          <Icon size={13} strokeWidth={2.2} />
        </span>
      ))}
    </div>
  );
}

function Cover({ t }) {
  return (
    <div className="sr-cov" data-t={t.key}>
      <span className="sr-cov-hex" aria-hidden="true" />
      <div className="sr-cov-head">
        <img src="/Niroggyan_logo.svg" alt="" width="92" height="15" />
        <span>Template {t.n}</span>
      </div>
      <div className="sr-cov-title">
        <span className="sr-cov-kick">Smart Health Report</span>
        <b>{t.name}</b>
        <span className="sr-cov-foot">{t.foot}</span>
      </div>
      <CoverArt k={t.key} />
      <ul className="sr-cov-promise">
        {COVER_PROMISES[t.key].map(([Icon, text]) => (
          <li key={text}><span><Icon size={12} strokeWidth={2.4} aria-hidden="true" /></span>{text}</li>
        ))}
      </ul>
      <div className="sr-cov-meta">
        <span><strong>{t.of}</strong> pages</span>
        <span>Powered by NirogGyan</span>
      </div>
    </div>
  );
}

// The last page of each template: the next real page, blurred, with a lock.
function Locked({ t }) {
  return (
    <div className="sr-lock">
      <img src={IMG(t.lock)} alt="" width="760" height="1076" decoding="async" />
      <div className="sr-lock-over">
        <span className="sr-lock-ic"><Lock size={20} aria-hidden="true" /></span>
        <b>{t.of - t.pages.length} more pages</b>
        <span>The full report is shared by our team.</span>
      </div>
    </div>
  );
}

function Compare() {
  const [pos, setPos] = useState(50);
  return (
    <div className="sr-wipe" style={{ "--pos": `${pos}%` }}>
      <Page k="thy" />
      <div className="sr-wipe-over" aria-hidden="true"><Page k="thybw" /></div>
      <span className="sr-wipe-tag" data-side="l" aria-hidden="true">Colour</span>
      <span className="sr-wipe-tag" data-side="r" aria-hidden="true">Mono</span>
      <span className="sr-wipe-bar" aria-hidden="true"><i><MoveHorizontal size={16} /></i></span>
      <input
        type="range" min="4" max="96" value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label="Compare the colour and mono versions of the same page"
        data-trapezium-id={IDS.compare}
      />
    </div>
  );
}

export default function SmartReports() {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const [tpl, setTpl] = useState("personal");
  const [card, setCard] = useState(DECK[0].key);
  const [prem, setPrem] = useState(PREMIUM[0].key);
  const [page, setPage] = useState(0); // the book's page, steps 1, 3 and 4 (Microbiology)

  const step = STEPS[active];
  const template = TEMPLATES.find((t) => t.key === tpl);
  const picked = DECK.find((d) => d.key === card);
  const last = active === STEPS.length - 1;
  const nextProduct = nextProductAfter(SCREEN_ID);
  const idBase = `product-${SCREEN}`;

  const onMore = () => (last ? navigate(nextProduct.route) : showStep(active + 1));
  const showStep = (n) => { setActive(n); setPage(0); };
  const pickTemplate = (key) => { setTpl(key); setPage(0); };
  const k = step.key;
  // The state names the step by its key (as its show-step-<key> tab does), plus
  // the picked template, card or premium page where the step has one.
  const detail = { lengths: tpl, pack: card, premium: prem }[k];
  const state = detail ? `step-${k}-${detail}` : `step-${k}`;

  let visual;
  if (k === "pdf") {
    visual = <Flipbook key="pdf" pages={[<Page k="cover" />, <Page k="han3" />]} label="Sample report" ids={IDS.page} onPage={setPage} about={[ABOUT.cover, ABOUT.han3]} />;
  } else if (k === "lengths") {
    visual = (
      <Flipbook
        key={tpl}
        pages={[<Cover t={template} />, ...template.pages.map((k) => <Page k={k} />), <Locked t={template} />]}
        label={template.name}
        ids={IDS.page}
        onPage={setPage}
        about={[`The ${template.name} template's cover.`, ...template.pages.map((p) => ABOUT[p]), "The rest of the report is locked: talk to our team to see it."]}
        caption={(i) => (i === 0 ? `${template.name}: cover`
          : i <= template.pages.length ? `${template.name}: page ${i} of ${template.pages.length} shown`
            : `${template.name}: ${template.of - template.pages.length} more pages, locked`)}
      />
    );
  } else if (k === "pack") {
    visual = picked.pages ? (
      <Flipbook key={picked.key} pages={picked.pages.map((k) => <Page k={k} />)} label={picked.name} ids={IDS.page} onPage={setPage} about={picked.pages.map((p) => ABOUT[p])} />
    ) : (
      <div className="sr-flat" key={picked.key}><Page k={picked.img} /></div>
    );
  } else if (k === "premium") {
    visual = <Fan picked={prem} />;
  } else {
    visual = <Compare />;
  }

  return (
    <SaiProductFrame screenId={SCREEN_ID} state={state}>
      <div className="prod-page" data-stage="deliver">
        <p className="prod-kicker">
          <i aria-hidden="true"><FileText size={15} /></i>
          Smart Reports
          <span>· Deliver</span>
        </p>
        <h1>The report you already send, made readable.</h1>
        <p className="prod-lede">A report your patients understand and engage with.</p>
        <ul className="sr-figures" aria-label="Smart Reports in numbers">
          {FIGURES.map(([b, s]) => <li key={s}><b>{b}</b> {s}</li>)}
        </ul>

        <Showcase
          video={{ ...VIDEOS.smartReportShowreel.wide, title: VIDEOS.smartReportShowreel.title }}
          title="See the Smart Report in action"
          points={["Every panel, its own layout", "One wellness score", "Track every change", "Three lengths, same result", "Prints in black and white"]}
          soundId={IDS.showcaseSound}
          playId={IDS.playDemo}
          rate={0.8}
        />

        <StepTabs
          steps={STEPS.map((s) => s.nav)}
          active={active}
          onChange={showStep}
          idBase={idBase}
          trapeziumId={(i) => IDS.step(STEPS[i].key)}
          label="Smart Reports: steps"
        />

        <div className="prod-stage sr-stage" id={`${idBase}-panel`} role="tabpanel" aria-labelledby={`${idBase}-tab-${active}`}>
          <div className="prod-visual sr-visual" data-trapezium-view={k === "pdf" || k === "lengths" || (k === "pack" && picked.pages) ? `page-${page + 1}` : undefined}><Fragment key={active}>{visual}</Fragment></div>
          <div className="prod-copy">
            {/* Keyed: the step's words and choices re-enter; the Next button below stays put. */}
            <Fragment key={active}>
            <h2>{step.head}</h2>
            <p>{step.lede}</p>

            {k === "lengths" ? (
              <div className="sr-choices" role="group" aria-label="Report length">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.key} type="button" aria-pressed={tpl === t.key}
                    data-trapezium-id={IDS.template(t.key)} onClick={() => pickTemplate(t.key)}
                  >
                    <b>{t.name}</b>
                    <span>{t.of} pages · {t.foot}</span>
                  </button>
                ))}
              </div>
            ) : null}
            {k === "lengths" ? (
              <button type="button" className="sr-full" data-trapezium-id={IDS.fullReport} onClick={() => navigate("/next-step")}>
                <Lock size={15} aria-hidden="true" />See all {template.of} pages: talk to our team
                <ArrowRight size={16} aria-hidden="true" />
              </button>
            ) : null}

            {k === "pack" ? (
              <div className="sr-deck" role="group" aria-label="Specialised reports">
                {DECK.map((d) => (
                  <button
                    key={d.key} type="button" aria-pressed={card === d.key}
                    data-trapezium-id={IDS.card(d.key)} onClick={() => { setCard(d.key); setPage(0); }}
                  >
                    <b>{d.name}</b>
                    <span>{d.sub}</span>
                  </button>
                ))}
              </div>
            ) : null}

            {k === "premium" ? (
              <div className="sr-deck sr-premium" role="group" aria-label="Personalised insights pages">
                {PREMIUM.map((p) => (
                  <button
                    key={p.key} type="button" aria-pressed={prem === p.key}
                    data-trapezium-id={IDS.premium(p.key)} onClick={() => setPrem(p.key)}
                  >
                    <b>{p.name}</b>
                    <span>{p.sub}</span>
                  </button>
                ))}
              </div>
            ) : null}

            {step.points ? (
              <ul className="prod-points">
                {step.points.map(([t, s], i) => (
                  <li key={t} style={{ "--i": i }}>
                    <i aria-hidden="true"><Check size={14} strokeWidth={3} /></i>
                    <span><b>{t}</b><span>{s}</span></span>
                  </li>
                ))}
              </ul>
            ) : null}
            </Fragment>

            <button type="button" className="prod-next" data-trapezium-id={IDS.more} onClick={onMore}>
              {last ? `Next product: ${nextProduct.title}` : `Next: ${STEPS[active + 1].nav}`}
              <ArrowRight size={16} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </SaiProductFrame>
  );
}
